// ── Strategy Detail Page ──────────────────────────────────────────────
function StrategyDetailPage({ onToast, onNavigate, strategyId }) {
  const [strategy, setStrategy] = React.useState(null);
  const [loading,  setLoading]  = React.useState(false);
  const [error,    setError]    = React.useState(null);

  const fetchStrategy = React.useCallback(async () => {
    if (!strategyId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: sbErr } = await window.sbClient
        .from('strategies')
        .select('*')
        .eq('id', strategyId)
        .single();
      if (sbErr) throw new Error(sbErr.message);
      setStrategy(data || null);
    } catch (e) {
      setError(e.message || 'Failed to load strategy');
    } finally {
      setLoading(false);
    }
  }, [strategyId]);

  React.useEffect(() => { fetchStrategy(); }, [fetchStrategy]);

  // ── No strategyId provided ────────────────────────────────────────────
  if (!strategyId) {
    return (
      <EmptyState
        icon={Wallet}
        title="Select a strategy from your vault"
        subtitle="Open the Vault, pick a strategy, then return here to see its detail."
        action={
          <button className="btn btn-primary btn-sm" onClick={() => onNavigate?.('vault')}>
            Go to Vault
          </button>
        }
      />
    );
  }

  // ── Loading skeleton ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
        <div className="skeleton" style={{ height:64, borderRadius:'var(--r-lg)' }}/>
        <div className="skeleton" style={{ height:100, borderRadius:'var(--r-lg)' }}/>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:10 }}>
          {Array.from({ length:6 }).map((_,i) => (
            <div key={i} className="skeleton" style={{ height:72, borderRadius:'var(--r-lg)' }}/>
          ))}
        </div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────
  if (error) {
    return (
      <EmptyState
        icon={Activity}
        title="Failed to load strategy"
        subtitle={error}
        action={
          <button className="btn btn-primary btn-sm" onClick={fetchStrategy}>
            <RefreshCw size={11}/> Retry
          </button>
        }
      />
    );
  }

  // ── Empty result (fetch succeeded but returned nothing) ───────────────
  if (!strategy) {
    return (
      <EmptyState
        icon={Wallet}
        title="Select a strategy from your vault"
        subtitle="That strategy ID was not found. Return to the Vault and select another."
        action={
          <button className="btn btn-primary btn-sm" onClick={() => onNavigate?.('vault')}>
            Go to Vault
          </button>
        }
      />
    );
  }

  // ── Helpers ───────────────────────────────────────────────────────────
  const fmt  = (v) => v != null ? `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%` : '—';
  const fmtN = (v, dp = 2) => v != null ? Number(v).toFixed(dp) : '—';

  const pm   = strategy.performance_metrics || {};
  const sharpe      = pm.sharpe;
  const cagr        = pm.cagr;
  const maxDrawdown = pm.maxDrawdown;
  const winRate     = pm.winRate;
  const profitFactor= pm.profitFactor;

  const statusBadge = strategy.status === 'live' ? 'badge-green'
                    : strategy.status === 'paper' ? 'badge-yellow'
                    : 'badge-muted';

  const createdLabel = strategy.created_at
    ? new Date(strategy.created_at).toISOString().slice(0, 10)
    : '—';

  // ── Delete handler ────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete "${strategy.name}"? This cannot be undone.`)) return;
    try {
      const { error: sbErr } = await window.sbClient
        .from('strategies')
        .delete()
        .eq('id', strategyId);
      if (sbErr) throw new Error(sbErr.message);
      onToast?.('Strategy deleted');
      onNavigate?.('vault');
    } catch (e) {
      onToast?.(`Delete failed: ${e.message}`, 'error');
    }
  };

  // ── Share link handler ────────────────────────────────────────────────
  const handleShare = () => {
    const url = `${window.location.origin}/share.html?strategy=${encodeURIComponent(strategy.id)}`;
    navigator.clipboard?.writeText(url);
    onToast?.('Share link copied', url);
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <PageHdr
        icon={FileText} iconColor="#D946EF" iconBg="rgba(217,70,239,0.10)"
        title={strategy.name}
        subtitle={`${strategy.asset || 'Multi-asset'} · ${strategy.exchange || 'Kraken'} · Strategy ID ${strategy.id}`}
        action={
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => onNavigate?.('designer')}>
              <Layers size={11}/> Edit
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => onNavigate?.('backtest')}>
              <Play size={11}/> Backtest
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => onNavigate?.('tearsheet')}>
              <FileText size={11}/> Full tearsheet
            </button>
          </div>
        }
      />

      {/* Header banner */}
      <div style={{ background:'transparent', border:'1px solid var(--border-strong)', borderRadius:'var(--r-lg)', padding:'20px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:14 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
            <span className={`badge ${statusBadge}`}>{(strategy.status || 'inactive').toUpperCase()}</span>
            <span style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--muted)' }}>Inception · {createdLabel}</span>
          </div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:26, color:'var(--ink)', letterSpacing:'-0.018em', fontWeight:600 }}>{strategy.name}</div>
        </div>
        <div style={{ display:'flex', gap:32 }}>
          <div><div style={{ fontFamily:'var(--font-ui)', fontSize:10, color:'var(--muted)' }}>CAGR</div><div style={{ fontFamily:'var(--font-mono)', fontSize:24, color:'var(--green)', fontWeight:500 }}>{fmt(cagr)}</div></div>
          <div><div style={{ fontFamily:'var(--font-ui)', fontSize:10, color:'var(--muted)' }}>SHARPE</div><div style={{ fontFamily:'var(--font-mono)', fontSize:24, color:'#D946EF', fontWeight:500 }}>{fmtN(sharpe)}</div></div>
          <div><div style={{ fontFamily:'var(--font-ui)', fontSize:10, color:'var(--muted)' }}>MAX DD</div><div style={{ fontFamily:'var(--font-mono)', fontSize:24, color:'var(--red)', fontWeight:500 }}>{fmt(maxDrawdown)}</div></div>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:10 }}>
        <StatCard title="CAGR"           value={fmt(cagr)}                                      icon={TrendingUp} accent="green"/>
        <StatCard title="Sharpe"         value={fmtN(sharpe)}                                   icon={Brain}      accent="purple"/>
        <StatCard title="Max Drawdown"   value={fmt(maxDrawdown)}                               icon={BarChart2}  accent="red"/>
        <StatCard title="Win rate"       value={winRate != null ? (winRate*100).toFixed(1)+'%' : '—'} icon={Check} accent="green"/>
        <StatCard title="Profit factor"  value={fmtN(profitFactor)}                             icon={DollarSign} accent="purple"/>
        <StatCard title="Asset"          value={strategy.asset || '—'}                          icon={Activity}/>
      </div>

      {/* Equity preview + actions */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:12 }}>
        <div className="card">
          <div className="card-title">Equity curve (preview)</div>
          <Sparkline data={strategy.equity || Array.from({ length:30 }, (_,i) => 100 + i*1.8 + Math.sin(i/3)*4)} color="#11ff99" height={140}/>
          <div style={{ marginTop:10, fontFamily:'var(--font-ui)', fontSize:12, color:'var(--muted)' }}>
            View full tearsheet for monthly returns, drawdown curve and trade log →
          </div>
        </div>
        <div className="card">
          <div className="card-title">Actions</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <button className="btn btn-ghost btn-sm" style={{ justifyContent:'flex-start' }} onClick={() => onNavigate?.('optimize')}><Cpu size={11}/> Optimize parameters</button>
            <button className="btn btn-ghost btn-sm" style={{ justifyContent:'flex-start' }} onClick={() => onNavigate?.('stress')}><Shield size={11}/> Run stress test</button>
            <button className="btn btn-ghost btn-sm" style={{ justifyContent:'flex-start' }} onClick={() => onNavigate?.('compare')}><GitMerge size={11}/> Compare with another</button>
            <button className="btn btn-ghost btn-sm" style={{ justifyContent:'flex-start' }} onClick={handleShare}><Share size={11}/> Copy public share link</button>
            <button className="btn btn-ghost btn-sm" style={{ justifyContent:'flex-start' }} onClick={() => window.open(`../share.html?strategy=${encodeURIComponent(strategy.id)}`, '_blank')}><ExternalLink size={11}/> Open public view</button>
            <button className="btn btn-ghost btn-sm" style={{ justifyContent:'flex-start' }} onClick={() => window.navigateTo('vault')}><Store size={11}/> List on marketplace</button>
            <button className="btn btn-danger btn-sm" style={{ justifyContent:'flex-start', marginTop:6 }} onClick={handleDelete}><Trash size={11}/> Delete strategy</button>
          </div>
        </div>
      </div>

      {/* ── Audit Trail: real run history from Supabase ──────────────── */}
      <RunHistoryTrail strategyId={strategy.id} onNavigate={onNavigate} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Run history browser — pulls backtest/optimize/stress runs from Supabase
// linked to this strategy, shows them as a timeline with replay buttons.
// ────────────────────────────────────────────────────────────────────────
function RunHistoryTrail({ strategyId, onNavigate }) {
  const [runs, setRuns] = React.useState({ bt:[], opt:[], stress:[], loading:true, error:null });

  const fetchRuns = React.useCallback(async () => {
    if (!window.sbClient || !strategyId) {
      setRuns({ bt:[], opt:[], stress:[], loading:false, error:null });
      return;
    }
    setRuns(prev => ({ ...prev, loading:true, error:null }));
    try {
      const [bt, opt, st] = await Promise.all([
        window.sbClient.from('backtest_runs').select('*').eq('strategy_id', strategyId).order('created_at', { ascending:false }).limit(20),
        window.sbClient.from('optimization_runs').select('*').eq('strategy_id', strategyId).order('created_at', { ascending:false }).limit(20),
        window.sbClient.from('stress_runs').select('*').eq('strategy_id', strategyId).order('created_at', { ascending:false }).limit(20),
      ]);
      setRuns({ bt: bt.data || [], opt: opt.data || [], stress: st.data || [], loading:false, error:null });
    } catch (e) {
      setRuns({ bt:[], opt:[], stress:[], loading:false, error: e.message || 'Failed to load run history' });
    }
  }, [strategyId]);

  React.useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Interleave by created_at desc
  const timeline = React.useMemo(() => {
    const all = [
      ...runs.bt.map(r => ({ ...r, kind:'backtest' })),
      ...runs.opt.map(r => ({ ...r, kind:'optimization' })),
      ...runs.stress.map(r => ({ ...r, kind:'stress' })),
    ];
    return all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [runs]);

  if (runs.loading) {
    return (
      <div className="card" style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:20 }}>
        <RefreshCw size={14} color="#3b9eff" style={{ animation:'spin 1s linear infinite' }}/>
        <span style={{ fontFamily:'var(--font-ui)', fontSize:12, color:'var(--muted)' }}>Loading run history…</span>
      </div>
    );
  }

  if (runs.error) {
    return (
      <div className="card" style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:10, padding:24 }}>
        <span style={{ fontFamily:'var(--font-ui)', fontSize:13, color:'var(--red)' }}>Failed to load run history: {runs.error}</span>
        <button className="btn btn-ghost btn-sm" onClick={fetchRuns}><RefreshCw size={11}/> Retry</button>
      </div>
    );
  }

  if (!timeline.length) {
    return (
      <div className="card">
        <div className="card-title">Run history · audit trail</div>
        <div style={{ padding:'16px 0', fontFamily:'var(--font-ui)', fontSize:13, color:'var(--muted)', textAlign:'center' }}>
          No runs yet for this strategy. Run a backtest to start building an audit trail.
        </div>
      </div>
    );
  }

  const colors     = { backtest:'#11ff99', optimization:'#D946EF', stress:'#3b9eff' };

  return (
    <div className="card">
      <div className="card-title" style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
        <span>Run history · audit trail</span>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--muted)' }}>{timeline.length} runs · {runs.bt.length} BT · {runs.opt.length} OPT · {runs.stress.length} STR</span>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:0, marginTop:10 }}>
        {timeline.map((r, i) => {
          const m = r.metrics || r.best_metrics || r.risk_metrics || {};
          const summary = r.kind === 'backtest'     ? `Sharpe ${(m.sharpe||0).toFixed(2)} · DD ${((m.maxDrawdown||0)*100).toFixed(1)}%`
                        : r.kind === 'optimization' ? `Best ${r.objective || 'fitness'} ${(m.bestFitness||0).toFixed(2)}`
                        : r.kind === 'stress'       ? `Score ${r.results ? Object.keys(r.results).length : '?'} scenarios`
                        : '';
          const date = new Date(r.created_at);
          const ago  = (() => {
            const ms = Date.now() - date.getTime();
            const h  = ms / 3600000;
            if (h < 1)  return Math.round(h * 60) + 'm ago';
            if (h < 24) return Math.round(h) + 'h ago';
            return Math.round(h / 24) + 'd ago';
          })();
          return (
            <div key={r.id} style={{ display:'grid', gridTemplateColumns:'100px 90px 1fr 200px auto', gap:14, padding:'10px 0', alignItems:'center', borderBottom: i < timeline.length-1 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--subtle)' }}>{ago}</span>
              <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                <span style={{ width:6, height:6, borderRadius:'50%', background:colors[r.kind] }}/>
                <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:colors[r.kind], textTransform:'uppercase', letterSpacing:'.05em' }}>{r.kind}</span>
              </span>
              <span style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--muted)' }}>{r.id.slice(0, 8)}</span>
              <span style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--ink)' }}>{summary}</span>
              <button className="btn btn-ghost btn-sm" style={{ padding:'3px 10px', fontSize:11 }}
                onClick={() => {
                  window.GQ?.state?.cacheRun(r);
                  const page = r.kind === 'backtest' ? 'tearsheet'
                             : r.kind === 'optimization' ? 'optimize' : 'stress';
                  window.navigateTo(page, { run: r.id });
                }}>
                Re-open →
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

window.StrategyDetailPage = StrategyDetailPage;
